"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, CreditCard, ExternalLink, LogOut, Plus, ScrollTextIcon, Sparkles, UploadIcon, FileTextIcon } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { SidebarLoungePoints } from "@/components/lounge/sidebar-points";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { Wordmark } from "@/components/ui/wordmark";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { ChatSidebarSkeleton } from "./chat-sidebar-skeleton";
import { SidebarChatSearch, SidebarShortcutKbd, useSidebarShortcut } from "./sidebar-actions";
import { StudioNav } from "./studio-nav";
import type { Skill } from "@/hooks/useSkills";
import type { Organization } from "@/lib/types";
interface SkillsSidebarProps {
  skills: Skill[];
  selectedSkillId: string | null;
  onSelectSkill: (skillId: string) => void;
  isLoading?: boolean;
  onCreateOpen: () => void;
  onGenerateOpen: () => void;
  onUploadOpen: () => void;
  selectedOrganization: Organization | null;
  className?: string;
}
export function SkillsSidebar(t0) {
  const $ = _c(91);
  const {
    skills,
    selectedSkillId,
    onSelectSkill,
    isLoading,
    onCreateOpen,
    onGenerateOpen,
    onUploadOpen,
    selectedOrganization,
    className
  } = t0;
  const router = useRouter();
  const queryClient = useQueryClient();
  const {
    state: sidebarState,
    isMobile
  } = useSidebar();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const isMac = useSidebarShortcut("j", onCreateOpen);
  let t1;
  if ($[0] !== queryClient || $[1] !== router || $[2] !== signOut) {
    t1 = async () => {
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {}
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            queryClient.clear();
            router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : "https://chat.llmgateway.io/login");
          }
        }
      });
    };
    $[0] = queryClient;
    $[1] = router;
    $[2] = signOut;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const logout = t1;
  const isHistoryHidden = sidebarState === "collapsed" && !isMobile;
  if (isUserLoading) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <ChatSidebarSkeleton organization={null} isOrgLoading={false} />;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  if (!user) {
    let t2;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}><Wordmark /></Link>;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    let t4;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="font-medium mb-2">Sign in required</div>;
      t4 = <p className="text-muted-foreground mb-3">Please sign in to view your skills.</p>;
      $[6] = t3;
      $[7] = t4;
    } else {
      t3 = $[6];
      t4 = $[7];
    }
    let t5;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t2}<div className="w-full rounded-md border p-4 text-sm">{t3}{t4}<div className="flex items-center justify-end gap-2"><Button size="sm" asChild={true}><Link href="/login">Sign in</Link></Button></div></div></div><StudioNav /></SidebarHeader>;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] !== className) {
      t6 = <Sidebar className={className}>{t5}</Sidebar>;
      $[9] = className;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    return t6;
  }
  let t2;
  if ($[11] !== className) {
    t2 = cn(className, "max-md:hidden");
    $[11] = className;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  let t3;
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SidebarMenuItem><SidebarMenuButton size="lg" asChild={true} tooltip="Lounge"><Link href="/" prefetch={true}><Wordmark size="sm" iconBox={true} /></Link></SidebarMenuButton></SidebarMenuItem>;
    t4 = <SidebarChatSearch disabled={true} />;
    $[13] = t3;
    $[14] = t4;
  } else {
    t3 = $[13];
    t4 = $[14];
  }
  let t5;
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Plus className="h-4 w-4" />;
    t6 = <span>New Skill</span>;
    $[15] = t5;
    $[16] = t6;
  } else {
    t5 = $[15];
    t6 = $[16];
  }
  const t7 = isMac ? "\u2318J" : "Alt+J";
  let t8;
  if ($[17] !== t7) {
    t8 = <SidebarShortcutKbd keys={t7} />;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ChevronDown className="ml-auto h-3 w-3 text-muted-foreground" />;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8) {
    t10 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton tooltip="New Skill" className="border border-border">{t5}{t6}{t8}{t9}</SidebarMenuButton></DropdownMenuTrigger>;
    $[20] = t8;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <FileTextIcon className="mr-2 h-4 w-4" />;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== onCreateOpen) {
    t12 = <DropdownMenuItem onClick={onCreateOpen}>{t11}Write skill</DropdownMenuItem>;
    $[23] = onCreateOpen;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Sparkles className="mr-2 h-4 w-4" />;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== onGenerateOpen) {
    t14 = <DropdownMenuItem onClick={onGenerateOpen}>{t13}Generate with AI</DropdownMenuItem>;
    $[26] = onGenerateOpen;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <UploadIcon className="mr-2 h-4 w-4" />;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== onUploadOpen) {
    t16 = <DropdownMenuItem onClick={onUploadOpen}>{t15}Upload a skill</DropdownMenuItem>;
    $[29] = onUploadOpen;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t12 || $[32] !== t14 || $[33] !== t16) {
    t17 = <DropdownMenuContent side="right" align="start" className="w-48">{t12}{t14}{t16}</DropdownMenuContent>;
    $[31] = t12;
    $[32] = t14;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t10 || $[36] !== t17) {
    t18 = <SidebarMenu>{t3}{t4}<SidebarMenuItem><DropdownMenu>{t10}{t17}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[35] = t10;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <StudioNav />;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t18) {
    t20 = <SidebarHeader>{t18}{t19}</SidebarHeader>;
    $[39] = t18;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div><div className="mx-2 mb-2 border-t border-sidebar-border" /></div>;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">My Skills</div>;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  let t23;
  if ($[43] !== isLoading || $[44] !== onSelectSkill || $[45] !== selectedSkillId || $[46] !== skills) {
    t23 = isLoading ? <div className="flex items-center justify-center py-8"><span className="text-muted-foreground text-sm">Loading skills...</span></div> : skills.length === 0 ? <div className="flex flex-col items-center justify-center py-8 px-4 text-center"><ScrollTextIcon className="h-8 w-8 text-muted-foreground/50 mb-3" /><p className="text-sm text-muted-foreground">No skills yet</p></div> : <SidebarMenu className="px-2">{skills.map(skill => <SidebarMenuItem key={skill.id}><SidebarMenuButton isActive={selectedSkillId === skill.id} onClick={() => onSelectSkill(skill.id)} tooltip={skill.name} className="h-auto py-2"><div className="flex-1 min-w-0"><div className="truncate text-sm font-medium">{skill.name}</div>{skill.description && <div className="truncate text-xs text-muted-foreground">{skill.description}</div>}</div></SidebarMenuButton></SidebarMenuItem>)}</SidebarMenu>;
    $[43] = isLoading;
    $[44] = onSelectSkill;
    $[45] = selectedSkillId;
    $[46] = skills;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== isHistoryHidden || $[49] !== t23) {
    t24 = <SidebarContent className="overflow-hidden pb-2">{t21}<div aria-hidden={isHistoryHidden} className="flex min-h-0 flex-1 flex-col transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0">{t22}{t23}</div></SidebarContent>;
    $[48] = isHistoryHidden;
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <SidebarLoungePoints />;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== selectedOrganization) {
    t26 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={selectedOrganization} isLoading={false} /></div>;
    $[52] = selectedOrganization;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  const t27 = user.name ?? "User";
  let t28;
  if ($[54] !== user.name) {
    t28 = user.name?.split(" ").map(_temp).join("").toUpperCase().slice(0, 2) ?? "U";
    $[54] = user.name;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== t28) {
    t29 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t28}</span></div>;
    $[56] = t28;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] !== user.name) {
    t30 = <span className="truncate font-semibold">{user.name}</span>;
    $[58] = user.name;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] !== user.email) {
    t31 = <span className="truncate text-xs text-muted-foreground">{user.email}</span>;
    $[60] = user.email;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  let t32;
  if ($[62] !== t30 || $[63] !== t31) {
    t32 = <div className="grid flex-1 text-left text-sm leading-tight">{t30}{t31}</div>;
    $[62] = t30;
    $[63] = t31;
    $[64] = t32;
  } else {
    t32 = $[64];
  }
  let t33;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />;
    $[65] = t33;
  } else {
    t33 = $[65];
  }
  let t34;
  if ($[66] !== t27 || $[67] !== t29 || $[68] !== t32) {
    t34 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t27} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t29}{t32}{t33}</SidebarMenuButton></DropdownMenuTrigger>;
    $[66] = t27;
    $[67] = t29;
    $[68] = t32;
    $[69] = t34;
  } else {
    t34 = $[69];
  }
  let t35;
  let t36;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <DropdownMenuItem asChild={true}><Link href="/pricing" prefetch={true}><CreditCard className="mr-2 h-4 w-4" />{"Membership & Billing"}</Link></DropdownMenuItem>;
    t36 = <DropdownMenuSeparator />;
    $[70] = t35;
    $[71] = t36;
  } else {
    t35 = $[70];
    t36 = $[71];
  }
  let t37;
  let t38;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <DropdownMenuItem asChild={true}><a href={process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : "https://llmgateway.io/dashboard"} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Dashboard</a></DropdownMenuItem>;
    t38 = <DropdownMenuSeparator />;
    $[72] = t37;
    $[73] = t38;
  } else {
    t37 = $[72];
    t38 = $[73];
  }
  let t39;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <span>Theme</span>;
    $[74] = t39;
  } else {
    t39 = $[74];
  }
  let t40;
  let t41;
  if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <DropdownMenuItem className="justify-between gap-3" onSelect={_temp2}>{t39}<div onClick={_temp3} onKeyDown={_temp4}><ThemeToggle className="shrink-0" size="compact" /></div></DropdownMenuItem>;
    t41 = <DropdownMenuSeparator />;
    $[75] = t40;
    $[76] = t41;
  } else {
    t40 = $[75];
    t41 = $[76];
  }
  let t42;
  if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <LogOut className="mr-2 h-4 w-4" />;
    $[77] = t42;
  } else {
    t42 = $[77];
  }
  let t43;
  if ($[78] !== logout) {
    t43 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t35}{t36}{t37}{t38}{t40}{t41}<DropdownMenuItem onClick={logout}>{t42}Log out</DropdownMenuItem></DropdownMenuContent>;
    $[78] = logout;
    $[79] = t43;
  } else {
    t43 = $[79];
  }
  let t44;
  if ($[80] !== t34 || $[81] !== t43) {
    t44 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t34}{t43}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[80] = t34;
    $[81] = t43;
    $[82] = t44;
  } else {
    t44 = $[82];
  }
  let t45;
  if ($[83] !== t26 || $[84] !== t44) {
    t45 = <SidebarFooter>{t25}{t26}{t44}</SidebarFooter>;
    $[83] = t26;
    $[84] = t44;
    $[85] = t45;
  } else {
    t45 = $[85];
  }
  let t46;
  if ($[86] !== t2 || $[87] !== t20 || $[88] !== t24 || $[89] !== t45) {
    t46 = <Sidebar collapsible="icon" className={t2}>{t20}{t24}{t45}</Sidebar>;
    $[86] = t2;
    $[87] = t20;
    $[88] = t24;
    $[89] = t45;
    $[90] = t46;
  } else {
    t46 = $[90];
  }
  return t46;
}
function _temp4(event_1) {
  return event_1.stopPropagation();
}
function _temp3(event_0) {
  return event_0.stopPropagation();
}
function _temp2(event) {
  return event.preventDefault();
}
function _temp(n) {
  return n[0];
}